import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

interface MetricCardProps {
  label: string;
  actualValue: number | null;
  projectedValue: number | null;
  format: 'percentage' | 'multiple';
  isLoading?: boolean;
  showProjectedFirst?: boolean; // For scenario tabs
}

export const MetricCard = ({ label, actualValue, projectedValue, format, isLoading, showProjectedFirst = false }: MetricCardProps) => {
  const formatValue = (value: number | null) => {
    if (value === null) return '-';
    if (format === 'percentage') return `${value.toFixed(2)}%`;
    return `${value.toFixed(2)}x`;
  };

  const delta = actualValue !== null && projectedValue !== null ? projectedValue - actualValue : null;
  const hasDelta = delta !== null && Math.abs(delta) > 0.01;
  const isPositive = delta !== null && delta > 0;

  if (showProjectedFirst && hasDelta) {
    // Scenario view: Projected is hero
    return (
      <Card sx={{ height: '100%', minHeight: 90 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary" gutterBottom>
            {label}
          </Typography>

          {/* Projected Value (Hero) */}
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h3" component="div" color="primary.main">
              {isLoading ? '...' : formatValue(projectedValue)}
            </Typography>
            <Chip
              label={`${isPositive ? '+' : ''}${formatValue(delta)}`}
              size="small"
              color={isPositive ? 'success' : 'error'}
              icon={isPositive ? <TrendingUp /> : <TrendingDown />}
              sx={{ height: 24 }}
            />
          </Box>

          {/* Actual Value (Secondary) */}
          <Typography variant="body2" color="text.secondary">
            Actual: {formatValue(actualValue)}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Actual view: Actual is hero
  return (
    <Card sx={{ height: '100%', minHeight: 90 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary" gutterBottom>
          {label}
        </Typography>

        {/* Actual Value */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Actual
          </Typography>
          <Typography variant="h4" component="div">
            {isLoading ? '...' : formatValue(actualValue)}
          </Typography>
        </Box>

        {/* Projected Value & Delta */}
        {hasDelta && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Projected
            </Typography>
            <Typography variant="h6" color="primary.main">
              {formatValue(projectedValue)}
            </Typography>
            <Chip
              label={`${isPositive ? '+' : ''}${formatValue(delta)}`}
              size="small"
              color={isPositive ? 'success' : 'error'}
              icon={isPositive ? <TrendingUp /> : <TrendingDown />}
              sx={{ height: 24 }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
